import { FC } from 'react'
import { LuPartyPopper } from 'react-icons/lu'
import { FaRegEnvelope } from 'react-icons/fa'
import { IoMdArrowBack } from 'react-icons/io'
import CustomText from '../../CustomText'
import BackButton from '../BackButton/BackButton'
import { Button, Flex } from '@chakra-ui/react'
import { NewCustomerType } from '../../../types'
import { DateTime } from 'luxon'
import { useAuthContext } from '../../../context/AuthProvider'

type ConfirmStepProps = {
  stepCount: number
  totalStep: number
  title: string
  description: string
  handleBack: () => void
  handleNext: () => void
  newCustomer?: NewCustomerType
}

export const ConfirmStep: FC<ConfirmStepProps> = ({
  stepCount,
  totalStep,
  title,
  description,
  handleBack,
  handleNext,
  newCustomer
}: ConfirmStepProps) => {
  const ms = Date.now()

  const { user } = useAuthContext()

  return (
    <div className={`w-full h-auto flex flex-col gap-y-4 items-center`}>
      <div className="w-[80px] h-[80px] bg-[#FFD200] flex flex-row items-center justify-center rounded-full">
        <LuPartyPopper className="w-[32px] h-[32px]" color="#ffffff" />
      </div>
      <CustomText
        textLabel={`Invite sent to ${newCustomer?.firstName}.`}
        fontWeight={`font-bold`}
        fontSize="text-[28px]"
        fontColor={`text-black`}
      />

      <div className="w-[44%] flex flex-row justify-center">
        <p className={`font-roboto font-poppins text-black font-regular text-[14px] text-center`}>
          We've emailed a unique onboarding link to <strong>{newCustomer?.email}. </strong>
          {new Intl.DateTimeFormat('en-US', {
            dateStyle: 'full',
            timeStyle: 'short'
          }).format(ms)}
        </p>
      </div>

      <div className="w-full h-auto flex flex-col gap-y-[25px] bg-white rounded-[32px] shadow-sm border-solid border-[1.5px] border-[#e4e4e4] p-[32px]">
        <CustomText
          textLabel={`Confirmation summary`}
          fontWeight={`font-semibold`}
          fontSize="text-[14px]"
          fontColor={`text-black`}
        />

        <div className="w-full h-auto flex flex-row gap-x-[24px]">
          <div
            className={`w-[50%] h-auto flex flex-col gap-y-4 p-[20px] rounded-2xl bg-[#FCFBF6] border-solid border-[1.5px] border-[#e4e4e4]`}
          >
            <div className="w-full h-auto flex flex-row justify-between">
              <CustomText
                textLabel={`CURRENT OWNER`}
                fontWeight={`font-semibold`}
                fontSize="text-[14px]"
                fontColor={`text-black`}
              />
            </div>

            <div className="w-full h-auto flex flex-row justify-between">
              <CustomText
                textLabel={`Name`}
                fontWeight={`font-regular`}
                fontSize="text-[12px]"
                fontColor={`text-black`}
              />
              <CustomText
                textLabel={`${user?.info?.firstName} ${user?.info?.lastName}` || ''}
                fontWeight={`font-bold`}
                fontSize="text-[14px]"
                fontColor={`text-black`}
              />
            </div>
            <div className="w-full h-auto flex flex-row justify-between">
              <CustomText
                textLabel={`Email`}
                fontWeight={`font-regular`}
                fontSize="text-[12px]"
                fontColor={`text-black`}
              />
              <CustomText
                textLabel={`${user?.info?.email}` || ''}
                fontWeight={`font-bold`}
                fontSize="text-[14px]"
                fontColor={`text-black`}
              />
            </div>
            <div className="w-full h-auto flex flex-row justify-between">
              <CustomText
                textLabel={`Mobile`}
                fontWeight={`font-regular`}
                fontSize="text-[12px]"
                fontColor={`text-black`}
              />
              <CustomText
                textLabel={`${user?.info?.contactNumber}` || ''}
                fontWeight={`font-bold`}
                fontSize="text-[14px]"
                fontColor={`text-black`}
              />
            </div>
            <div className="w-full h-auto flex flex-row justify-between">
              <CustomText
                textLabel={`Move date`}
                fontWeight={`font-regular`}
                fontSize="text-[12px]"
                fontColor={`text-black`}
              />
              <CustomText
                textLabel={`${DateTime.fromISO(newCustomer?.transferDate as string).toFormat('dd LLLL yyyy')}`}
                fontWeight={`font-bold`}
                fontSize="text-[14px]"
                fontColor={`text-black`}
              />
            </div>
          </div>
          <div
            className={`w-[50%] h-auto flex flex-col gap-y-4 p-[20px] rounded-2xl bg-[#FCFBF6] border-solid border-[1.5px] border-[#e4e4e4]`}
          >
            <div className="w-full h-auto flex flex-row justify-between">
              <CustomText
                textLabel={`WHAT HAPPENS NEXT`}
                fontWeight={`font-semibold`}
                fontSize="text-[14px]"
                fontColor={`text-black`}
              />
            </div>

            <div className="w-full h-auto flex flex-row justify-between">
              <p
                className={`font-roboto font-poppins text-black font-regular text-[14px] text-center`}
              >
                1. <strong>{newCustomer?.firstName}</strong> opens the onboarding link.
              </p>
            </div>
            <div className="w-full h-auto flex flex-row justify-between">
              <CustomText
                textLabel={`2. They complete credit check, banking, ID upload and debit order date.`}
                fontWeight={`font-regular`}
                fontSize="text-[14px]"
                fontColor={`text-black`}
              />
            </div>
            <div className="w-full h-auto flex flex-row justify-between">
              <CustomText
                textLabel={`3. We confirm the switch and notify you both.`}
                fontWeight={`font-regular`}
                fontSize="text-[14px]"
                fontColor={`text-black`}
              />
            </div>
          </div>
        </div>

        <div
          className={`w-full h-auto flex flex-col gap-y-4 p-[20px] rounded-full bg-[#FCFBF6] border-solid border-[1.5px] border-[#e4e4e4]`}
        >
          <div className="w-full h-auto flex flex-row gap-x-[12px]">
            <FaRegEnvelope className="w-[20px] h-[20px]" color="#000000" />

            <p className={`font-roboto font-poppins text-black font-regular text-[14px]`}>
              A confirmation has been emailed to <strong>{newCustomer?.email}.</strong>
            </p>
          </div>
        </div>
      </div>

      <div className="w-full h-auto flex flex-row justify-between items-center">
        <div className="w-fit h-fit flex flex-row justify-between">
          <BackButton label="Back" handleClick={handleBack} isBack={false}>
            <IoMdArrowBack className="w-[20px] h-[20px]" color="#000000" />
          </BackButton>
        </div>

        <Flex w="100%" h="100%" alignItems={'center'} justifyContent="flex-end">
          <Button variant={'dark'} isLoading={false} onClick={handleNext} type="submit" mt="15px">
            Next steps
          </Button>
        </Flex>
      </div>
    </div>
  )
}

export default ConfirmStep
